import * as React from 'react';

import { Col, Divider, Row } from 'antd';
import { useAppDispatch, useAppSelector } from '@/types/reduxHelpers';

import { AnimatedHeader } from '@/components/animatedHeader';
import { ExternalVisitor } from 'drawbridge.shared/models/dataModels/externalVisitor';
import { PageWrapper } from '@/components/pageWrapper';
import { ReturningVisitorForm } from '@/components/forms';
import { WelcomeHeader } from '@/components/pageHeader';
import { Zoom } from 'react-awesome-reveal';
import { setVisitor } from '@/store/modules/signInModule';
import { useAppRoutes } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVendorValidation } from '@/hooks/useVendorValidation';

interface ReturningVisitorPageProps {

}

export const ReturningVisitorPage: React.FC<ReturningVisitorPageProps> = (props) => {
    const { t } = useTranslation();

    const appRoutes = useAppRoutes();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [isLoading, setIsLoading] = React.useState(false);

    const vendorValidation = useVendorValidation();
    const isPerformingWork = useAppSelector((state) => state.signIn.isPerformingWork);
    const visitDetails = useAppSelector((state) => state.signIn.visitDetails);

    const visitor = useAppSelector((state) => state.signIn.visitor);
    const externalVisitor = !visitor
        ? undefined
        : visitor.visitorType === 'external'
            ? visitor
            : undefined;

    React.useEffect(() => {
        if (isPerformingWork === undefined || !visitDetails) {
            // There's no way to legitimately get here without these values unless pages were skipped
            // Just go home and reset the sign in state...
            navigate(appRoutes.home.path, { replace: true });

        }
    }, []);

    if (isPerformingWork === undefined || !visitDetails) {
        return <></>;
    }

    return (
        <PageWrapper
            pageTitle={<WelcomeHeader />}
            isNonIdeal={vendorValidation.vendorValidationFailed}
            nonIdealHeader={t('Types.VendorValidationMessage.validation_failed_header')}
            nonIdealActions={[
                { text: t('Types.VendorValidationMessage.validation_failed_restart_button_text'), action: () => navigate(appRoutes.home.path) },
            ]}
            nonIdealSubheader={(
                <div>
                    <div style={{ textAlign: 'center' }}>
                        {t('Types.VendorValidationMessage.validation_failed_message')}
                    </div>

                    <Divider />

                    <ul style={{ listStyleType: 'none' }}>
                        {vendorValidation.localizedMessages.map((validationMessage, index) => {
                            return (
                                <li key={index}>
                                    {validationMessage}
                                </li>
                            );
                        })}
                    </ul>

                    <Divider />

                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {t('Types.VendorValidationMessage.validation_failed_description')}
                    </div>
                </div>
            )}
        >
            <Row
                align="middle"
                justify="space-around"
                gutter={20}
            >
                <Col
                    span={24}
                >
                    <AnimatedHeader>
                        {t('Pages.ReturningVisitorPage.header')}
                    </AnimatedHeader>
                </Col>

                <Col
                    xxl={16}
                    xl={18}
                    lg={18}
                    md={24}
                    sm={24}
                    xs={24}
                >
                    <Zoom
                        duration={300}
                        triggerOnce={true}
                    >
                        <ReturningVisitorForm
                            isLoading={isLoading}
                            // Must have an ID (i.e. already be saved) to be a 'returning visitor'
                            externalVisitor={!!externalVisitor?.id ? externalVisitor as ExternalVisitor : undefined}
                            onFinish={async (values) => {
                                setIsLoading(true);

                                dispatch(setVisitor({
                                    ...values.externalVisitor,
                                }));

                                setIsLoading(false);

                                if (isPerformingWork) {
                                    if (!values.externalVisitor.vendorId) {
                                        // We need a verified vendor for the visitor if they're going to be performing work
                                        // Send them to the vendor update page to work that out
                                        navigate(appRoutes.returningVisitorVendorUpdate.path);
                                    } else {
                                        const validationResult = await vendorValidation.getVendorValidationResult({
                                            isPerformingWork: isPerformingWork,
                                            vendorId: values.externalVisitor.vendorId,
                                        });

                                        if (!validationResult?.isValid) {
                                            setIsLoading(false);

                                            return;
                                        }

                                        // Send them to the safety questionnaire
                                        navigate(appRoutes.safety.path);
                                    }

                                } else {
                                    // Not performing work, head to guest wifi page
                                    // navigate(appRoutes.globalHealthAndSafety.path);
                                    navigate(appRoutes.guestWifiPage.path);
                                }
                            }}
                        />
                    </Zoom>
                </Col>
            </Row>
        </PageWrapper>
    );
};